// If method is missing, add from Web/Mobile project or create a new one.

import dayjsDefault, { Dayjs, ManipulateType, OpUnitType } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isBetween from "dayjs/plugin/isBetween";
import weekOfYear from "dayjs/plugin/weekOfYear";
import weekday from "dayjs/plugin/weekday";
import minMax from "dayjs/plugin/minMax";
import "dayjs/locale/fi";

dayjsDefault.extend(utc);
dayjsDefault.extend(timezone);
dayjsDefault.extend(isSameOrAfter);
dayjsDefault.extend(isSameOrBefore);
dayjsDefault.extend(isBetween);
dayjsDefault.extend(weekOfYear);
dayjsDefault.extend(weekday);
dayjsDefault.extend(minMax);

dayjsDefault.locale("fi");

// Dayjs loses tz information when reinitiliazing existing dayjs-object when tz is UTC.
const dayjs = (date?: Date | Dayjs | string | number): Dayjs =>
    date instanceof dayjsDefault ? date as Dayjs : dayjsDefault(date || dayjsDefault());

export class Base {
    static isNullOrUndefined(test: any): boolean {
        return test === null || test === undefined;
    }

    static capitalizeFirstChar(s: string): string {
        if (!s) return s;
        return s.charAt(0).toUpperCase() + s.slice(1);
    }

    static right(s: string, l: number): string {
        return s.substr(s.length - l);
    }

    static leftPad(s: string, l: number, c = "0"): string {
        if (s.length > l - 1) return s;
        return Base.right(`${c.repeat(l)}${s}`, l);
    }

    // ------------------------------
    // Times and Dates
    // ------------------------------
    static timeZoneName(): string {
        return dayjsDefault.tz.guess();
    }

    static dateTzOffset(date: string | Dayjs | Date | number, tz: string): string {
        return dayjsDefault.tz(date, tz).format("Z");
    }

    // Compare date tz with local date tz at the same time to avoid different offset because of DST.
    static dateHasLocalTz(date: string, tz: string): boolean {
        return this.dateTzOffset(date, tz) !== this.dateTzOffset(date, this.timeZoneName());
    }

    // Returns date tz offset if different than local tz
    static dateTzOffsetIfNotLocal(date: string, tz: string): string {
        const offset = this.dateTzOffset(date, tz);
        return offset !== this.dateTzOffset(date, this.timeZoneName()) ? offset : "";
    }

    static dateToMinuteAccuracy(date?: string | Dayjs | Date | number): Dayjs {
        return this.dateStartOf(date, "minute");
    }

    static dateToDateJSON(date?: string | Dayjs | Date | number): string {
        return dayjs(date).format("YYYY-MM-DD");
    }

    static dateToJsonDateTime(date: string | Dayjs | Date | number): string {
        return dayjs(date).format("YYYY-MM-DDTHH:mm:ss");
    }

    static dateToJsonDateTimeWithOffset(date: string | Dayjs | Date | number): string {
        return dayjs(date).format("YYYY-MM-DDTHH:mm:ssZ");
    }

    static dateToDateStr(date: string | Dayjs | Date | number): string {
        return dayjs(date).format("D.M.YYYY");
    }

    static dateToShortDateStr(date: string | Dayjs | Date | number): string {
        return dayjs(date).format("D.M.");
    }

    static dateToTimeStr(date: string | Dayjs | Date | number): string {
        return dayjs(date).format("H.mm");
    }

    static hoursToTimeStr(hours: number) {
        return `${Math.floor(hours)}:${Math.round((hours % 1) * 60)}`;
    }

    static timeStrToHours(time: string) {
        const date = dayjsDefault(time, "HH:mm");
        return date.hour() + date.minute() / 60;
    }

    static dateToDateTimeStr(
        date: string | Dayjs | Date | number,
        includeSeconds = false
    ): string {
        return dayjs(date).format(
            "D.M.YYYY H.mm" + (includeSeconds ? ":ss" : "")
        );
    }

    static dateToDayDateTimeStr(
        date: string | Dayjs | Date | number,
        includeSeconds = false
    ): string {
        return dayjs(date).format(
            "dd D.M.YYYY H.mm" + (includeSeconds ? ":ss" : "")
        );
    }

    static dateToDateTimeStrWithTimezone(
        date: string | Dayjs | Date | number
    ): string {
        if (!date) return "";
        return dayjs(date).format();
    }

    static dateStrToDayjsIgnoreTimezone(dateStr: string) {
        if (!dateStr) return null;
        // Remove timezone information from str to avoid converting when initializing new object
        return dayjs(dateStr.split(/(Z|(\+|-)\d\d\:\d\d)$/gi)[0]);
    }

    static dateStrToOriginalTimezoneDateStr(dateStr: string): string {
        return this.dateToDateStr(this.dateStrToDayjsIgnoreTimezone(dateStr));
    }

    static dateStrToOriginalTimezoneDateStrShort(dateStr: string): string {
        return this.dateToShortDateStr(
            this.dateStrToDayjsIgnoreTimezone(dateStr)
        );
    }

    static dateStrToOriginalTimezoneTimeStr(dateStr: string): string {
        return this.dateToTimeStr(this.dateStrToDayjsIgnoreTimezone(dateStr));
    }

    static dateStrToOriginalTimezoneDateTimeStr(dateStr: string): string {
        return this.dateToDateTimeStr(
            this.dateStrToDayjsIgnoreTimezone(dateStr)
        );
    }

    static dateStrToOriginalTimezoneJsonDateTime(dateStr: string): string {
        return this.dateToJsonDateTime(
            this.dateStrToDayjsIgnoreTimezone(dateStr)
        );
    }

    static isSameDay(
        date1: string | Dayjs | Date | number,
        date2: string | Dayjs | Date | number
    ): boolean {
        return dayjs(date1).isSame(date2, "day");
    }

    static isSameOrAfter(
        date1: string | Dayjs | Date | number,
        date2: string | Dayjs | Date | number
    ): boolean {
        return dayjs(date1).isSameOrAfter(date2);
    }

    static isAfter(
        date1: string | Dayjs | Date | number,
        date2: string | Dayjs | Date | number,
        granularity?: OpUnitType,
    ): boolean {
        return dayjs(date1).isAfter(date2, granularity);
    }

    static getWeekDayNameLong(
        date: Date | number | Dayjs | string
    ): string {
        return dayjs(date).format("dddd");
    }

    static isOverlapping(
        startTimeA: string | number | Dayjs | Date,
        endTimeA: string | number | Dayjs | Date,
        startTimeB: string | number | Dayjs | Date,
        endTimeB: string | number | Dayjs | Date,
        inclusive = false
    ): boolean {
        return inclusive
            ? dayjs(startTimeA).isSameOrBefore(endTimeB) &&
                  dayjs(startTimeB).isSameOrBefore(endTimeA)
            : dayjs(startTimeA).isBefore(endTimeB) &&
                  dayjs(startTimeB).isBefore(endTimeA);
    }

    static isBetween(
        time: string | number | Dayjs | Date,
        rangeStart: string | number | Dayjs | Date,
        rangeEnd: string | number | Dayjs | Date,
        granularity?: OpUnitType,
        inclusivity?: "[]" | "()" | "[)" | "(]"
    ): boolean {
        return dayjs(time).isBetween(
            rangeStart,
            rangeEnd,
            granularity,
            inclusivity
        );
    }

    static dateDiff(date1: Dayjs, date2: Dayjs, unit: OpUnitType): number {
        if (Base.isNullOrUndefined(date1) || Base.isNullOrUndefined(date2))
            return 0;
        return date2.diff(date1, unit);
    }

    static dateDiffInMinutes(
        date1: Date | Dayjs | string | number,
        date2: Date | Dayjs | string | number
    ): number {
        if (Base.isNullOrUndefined(date1) || Base.isNullOrUndefined(date2))
            return 0;
        return this.dateDiff(dayjs(date1), dayjs(date2), "minutes");
    }

    static dateDiffInDays(
        date1: Date | Dayjs | string | number,
        date2: Date | Dayjs | string | number
    ): number {
        if (Base.isNullOrUndefined(date1) || Base.isNullOrUndefined(date2))
            return 0;
        return this.dateDiff(dayjs(date1), dayjs(date2), "days");
    }

    static dateStartOf(
        date: Date | Dayjs | string | number,
        unit: OpUnitType
    ): Dayjs {
        return dayjs(date).startOf(unit);
    }

    static dateEndOf(
        date: Date | Dayjs | string | number,
        unit: OpUnitType
    ): Dayjs {
        return dayjs(date).endOf(unit);
    }

    static dateAdd(date: Dayjs, value: number, unit: ManipulateType): Dayjs {
        return date.add(value, unit);
    }

    static dateAddMinutes(
        date: Date | Dayjs | string | number,
        value: number
    ): Dayjs {
        return this.dateAdd(dayjs(date), value, "minutes");
    }

    static dateAddDays(
        date: Date | Dayjs | string | number,
        value: number
    ): Dayjs {
        return this.dateAdd(dayjs(date), value, "days");
    }

    static dateAddMonths(
        date: Date | Dayjs | string | number,
        value: number
    ): Dayjs {
        return this.dateAdd(dayjs(date), value, "months");
    }

    static getWeekNumber(date: Date | Dayjs | string | number): number {
        return dayjs(date).week();
    }

    static hoursToMinutes(hours: number): number {
        if (Base.isNullOrUndefined(hours)) return 0;
        const value = hours ?? 0;
        const minutes = Math.round(value * 60);
        const hour = Math.floor(minutes / 60);
        const min = Math.round(minutes - hour * 60);
        return 60 * hour + min;
    }

    static minDate(dates: (string | Dayjs | Date | number)[]): Dayjs {
        return dayjsDefault.min(dates.map(d => dayjs(d)));
    }

    static maxDate(dates: (string | Dayjs | Date | number)[]): Dayjs {
        return dayjsDefault.max(dates.map(d => dayjs(d)));
    }

    // ------------------------------
    // Sorting
    // ------------------------------
    static numberCompare(a: number, b: number): number {
        if (Base.isNullOrUndefined(a) && Base.isNullOrUndefined(b)) return 0;
        if (Base.isNullOrUndefined(a)) return 1;
        if (Base.isNullOrUndefined(b)) return -1;
        return a - b;
    }

    static stringCompare(a: string, b: string): number {
        if (Base.isNullOrUndefined(a) && Base.isNullOrUndefined(b)) return 0;
        if (Base.isNullOrUndefined(a)) return 1;
        if (Base.isNullOrUndefined(b)) return -1;
        return a.localeCompare(b);
    }

    static booleanCompare(a: boolean, b: boolean): number {
        if (Base.isNullOrUndefined(a) && Base.isNullOrUndefined(b)) return 0;
        if (Base.isNullOrUndefined(a)) return 1;
        if (Base.isNullOrUndefined(b)) return -1;
        return (a ? 1 : 0) - (b ? 1 : 0);
    }

    static dateCompare(
        a: Date | Dayjs | string | number,
        b: Date | Dayjs | string | number
    ): number {
        if (Base.isNullOrUndefined(a) && Base.isNullOrUndefined(b)) return 0;
        if (Base.isNullOrUndefined(a)) return 1;
        if (Base.isNullOrUndefined(b)) return -1;
        return dayjs(a).isAfter(b) ? 1 : dayjs(a).isBefore(b) ? -1 : 0;
    }

    // ------------------------------
    // Generics
    // ------------------------------
    static getTypedArray<T>(items: any, type: new (...args: any[]) => T): T[] {
        const result: T[] = [];
        if (items) {
            for (let i = 0; i < items.length; i++) {
                /* eslint-disable new-cap */
                result.push(new type(items[i]));
                /* eslint-enable new-cap */
            }
        }
        return result;
    }

    static groupArray<T>(items: T[], key: string): { [key: string]: T[] } {
        if (!items || items.length < 1) return {};
        const result: { [key: string]: T[] } = {};
        for (const item of items) {
            (result[item[key]] = result[item[key]] || []).push(item);
        }
        return result;
    }

    //!!! USE ONLY FOR SIMPLE TYPES
    private static getUniqueItems<T>(items: T[]): T[] {
        return [...Array.from(new Set(items))];
    }

    // ------------------------------
    // Array
    // ------------------------------
    static getUniqueStringItems(items: string[]): string[] {
        return Base.getUniqueItems<string>(items);
    }

    // ------------------------------
    // Json
    // ------------------------------
    static isJsonString(str: string): boolean {
        if (!str) return false;
        try {
            const obj = JSON.parse(str);
            if (obj && typeof obj === "object") {
                return true;
            }
        } catch (e) {
            return false;
        }
        return true;
    }

    // https://developer.mozilla.org/en-US/docs/Web/API/WindowBase64/Base64_encoding_and_decoding
    static b64EncodeUnicode(str: string) {
        // first we use encodeURIComponent to get percent-encoded UTF-8,
        // then we convert the percent encodings into raw bytes which
        // can be fed into btoa.
        return window.btoa(
            encodeURIComponent(str).replace(
                /%([0-9A-F]{2})/g,
                function toSolidBytes(_, p1: string) {
                    return String.fromCharCode(parseInt(`0x${p1}`));
                }
            )
        );
    }
}
